<i18n>
{
  "en": {
    "upgrade": "Upgrade",
    "join": "Join",
    "reviews": "User Reviews",
    "click_to_join": "Click Here To Join"
  },
  "ja": {
    "upgrade": "アップグレード",
    "join": "入会する",
    "reviews": "ユーザーレビュー",
    "click_to_join": "入会はこちら"
  }
}
</i18n>

<template>
<main>
  <vue-headful
    :title="dynamicTitle"
    :head="{
        'meta[name=\'robots\']': {
          name: 'robots',
          content: (movie.isGuestExcluded) ? 'noindex' : 'index'
        },
    }" />
  <div v-if="movie.isGuestExcluded">
    <not-found />
  </div>
  <div v-else id="movies">
    <div class="movie-container">
      <div class="flex-container">
        <div class="content">
          <!-- video player -->
          <video-player :key="componentRefresh" />

          <!-- playlist menu -->
          <playlist-menu v-if="playlistEnabled" />

          <div class="movie-contents">
            <!-- movie info -->
            <movie-info :hide-release-date="hideOldReleaseDate" />

            <!-- movie download -->
            <download-menu v-if="!newXPEligible" />

            <!-- related movies -->
            <related-movies :num-movies="6" />
          </div>
        </div>

        <!-- side bar -->
        <sidebar />
      </div>
    </div>
  </div>

  <!-- join button -->
  <div class="mobile-join" v-if="!isSiteMember">
    <router-link to="/join/" class="button is-filled is-color-main is-fullwidth">{{ $t('click_to_join') }}<i class="iconf-arrow-right is-right"></i></router-link>
  </div>
</main>
</template>

<script>
/* eslint max-len: 0 */

import vueHeadful from 'vue-headful';
import BifrostAPI from '@/assets/js/services/Bifrost/API';
import MovieHelper from '@/assets/js/utils/movie';
import APP_CONFIG from '@/appConfig';
import VideoPlayer from '@/components/movieDetail/videoPlayer.vue';
import PlaylistMenu from '@/components/movieDetail/playlistMenu.vue';
import MovieInfo from '@/components/movieDetail/movieInfo.vue';
import DownloadMenu from '@/components/movieDetail/downloadMenu.vue';
import RelatedMovies from '@/components/movieDetail/relatedMovies.vue';
import Sidebar from '@/components/_shell/sidebar/index.vue';
import NotFound from '@/components/_shell/NotFound.vue';

export default {
  components: {
    'vue-headful': vueHeadful,
    'video-player': VideoPlayer,
    'playlist-menu': PlaylistMenu,
    'movie-info': MovieInfo,
    'download-menu': DownloadMenu,
    'related-movies': RelatedMovies,
    'not-found': NotFound,
    sidebar: Sidebar,
  },
  data() {
    return {
      movie: {},
      dynamicTitle: '',

      componentRefresh: 0,
    };
  },
  async mounted() {
    await this.getMovie(this.userTypeID);
  },
  methods: {
    async getMovie(userTypeID) {
      const bfAPI = new BifrostAPI();

      const movie = await bfAPI.getMovieDetail(this.$route.params.movieId);
      this.movie = MovieHelper.expandDetails(movie, this.locale, userTypeID);

      // redirect to home if the movie's no_list_display flag is set
      if (Object.prototype.hasOwnProperty.call(this.movie, 'NoListDisplay')
        && this.movie.NoListDisplay) this.$router.push({ name: 'home' });

      // set browser title
      const actors = (this.locale === 'en') ? this.movie.ActressesEn.join(', ') : this.movie.ActressesJa.join(' ');
      const title = (this.locale === 'en') ? this.movie.TitleEn : this.movie.Title;
      this.dynamicTitle = `${actors} ${title} ${APP_CONFIG.site.title[this.locale]}`;

      // we have the title, now send the page view
      this.$analytics.trackPage(this.$route.fullPath, this.dynamicTitle);
    },

    forceRerender() {
      // setting a reactive counter hooked up to the `key` attribute for the video-plahyer
      // component in the template. vue knows to re-render a component if it has a `key` attribute
      // bound to a reactive value, so we're taking advantage of this in order to force the player
      // to refresh whenever the user logs in -- this fixes a bug where two GA video player events
      // would be sent if a user logs in (or out) while on a movie detail page
      // (from https://michaelnthiessen.com/force-re-render/
      setTimeout(() => {
        this.componentRefresh += 1;
      }, 650);
    },
  },
  computed: {
    playlistEnabled() {
      return this.$store.getters['playlist/isEnabled'];
    },
    locale() {
      return this.$i18n.locale;
    },
    newXPEligible() {
      return this.$store.getters['user/isNewXPEligible'];
    },
    userTypeID() {
      return this.$store.getters['user/typeID'];
    },
    isSiteMember() {
      return this.$store.getters['user/isSiteMember'];
    },
    hasCensoredSample() {
      const releaseEpoch = Date.parse(this.movie.Release) / 1000;
      return (releaseEpoch >= 1489622400); // march 6 2017
    },
    hideOldReleaseDate() {
      const releaseDate = new Date(this.movie.Release);
      const currentDate = new Date();
      const differenceInMsec = currentDate - releaseDate;
      const differenceInDays = differenceInMsec / (1000 * 60 * 60 * 24); // milliseconds to days
      return (differenceInDays > APP_CONFIG.site.releaseDateHiddenAge);
    },
  },
  watch: {
    userTypeID(newVal) {
      this.getMovie(newVal);
      this.forceRerender();
    },
  },
};
</script>

<style lang="scss" scoped>
// this block needs to be 'scoped' because one of the global styles is causing the timeslider
// previews to not render correctly (shows the entire sprite instead of the frame)
@import '@/assets/sass/pages/_detail.scss';
</style>
